/**
 * BookIdentityCard — 单本书的身份卡片（封面 + 书编码 + 语言 + 书名）
 *
 * 运营在「选完书之后」要一眼认出选的是哪本。此前每个用到的地方各画一份，显示的字段
 * 也各不相同，故抽出这一个组件统一。
 *
 * **主名用 `cn_title`（书组级中文识别名）**：per-locale 的 `title` 是该语言行的原文书名，
 * 捷克语 / 匈牙利语行显示出来运营认不出是哪本；`cn_title` 跨 locale 稳定且是必填字段。
 * 原文书名降为次要信息，与作者同行 —— 要找的人仍看得到，但不占主位。
 *
 * 视觉沿用 `BookLanguageRowPicker` 既有的已选卡片，不引入新形态。
 */

import type { NovelResponse } from '@novel-admin/shared';
import type { ReactNode } from 'react';
import { bookCodeDisplay } from '~/lib/book-identity';
import { BookCover } from '~/ui/BookCover';
import { LangChip } from '~/ui/LangChip';

/**
 * 次要行里的原文书名 / 作者：与主名相同就不重复画（中文原创书两者一致）。
 *
 * 不特判 `author_display === 'undefined'` 这类脏值 —— 库里确实有一批（novel-app-server#977），
 * 但在展示层把它滤掉等于把数据问题固化成契约，订正归属在 C 端那侧。这里如实显示库里的值。
 */
function secondaryLine(book: NovelResponse): string {
  const parts: string[] = [];
  if (book.title && book.title !== book.cn_title) parts.push(book.title);
  if (book.author_display) parts.push(book.author_display);
  return parts.join(' · ');
}

export function BookIdentityCard({
  book,
  badge,
  fallback,
  maxWidth = 420,
}: {
  /** 已选中的 per-locale 书行；undefined = 未选中或还没查到，交给 `fallback` 画 */
  book: NovelResponse | undefined;
  /** 编码行尾部的额外徽标（如 picker 的「语言不匹配」警示） */
  badge?: ReactNode;
  /** 无 book 时显示的内容，由调用方决定措辞（未选 / 加载中 / 查不到是三种不同的话） */
  fallback?: ReactNode;
  maxWidth?: number | string;
}) {
  return (
    <div className="card-flat hstack" style={{ padding: '6px 10px', gap: 8, flex: 1, maxWidth }}>
      <BookCover src={book?.cover_url} hue={0} width={24} height={33} radius={3} />
      <div style={{ minWidth: 0, flex: 1 }}>
        {book ? (
          <>
            <div className="hstack" style={{ gap: 6 }}>
              <span className="mono" style={{ fontSize: 12, fontWeight: 500 }}>
                {bookCodeDisplay({
                  bookCode: book.book_code,
                  bookGroupCode: book.book_group_code,
                  locale: book.locale,
                })}
              </span>
              <LangChip code={book.locale} style={{ fontSize: 9.5 }} />
              {badge}
            </div>
            <div
              style={{
                fontSize: 12,
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {book.cn_title}
            </div>
            {secondaryLine(book) && (
              <div
                style={{
                  fontSize: 11,
                  color: 'var(--fg-muted)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {secondaryLine(book)}
              </div>
            )}
          </>
        ) : (
          fallback
        )}
      </div>
    </div>
  );
}
